import React, { useEffect, useState } from 'react';
import UserService from '../../service/UserService';
import BranchService from '../../service/BranchService';
import { Button, Modal, Form,Spinner} from 'react-bootstrap';
import { Trash2, Edit, UserPlus, Users, Search, ChevronLeft, ChevronRight, ShieldCheck, User, Eye } from 'lucide-react';
import '../../styles/view/ManageUsers.css';
import { useNavigate } from 'react-router-dom';

function getInitials(nombre = '', apellido = '') {
  return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase() || '?';
}

export default function ManageUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    nombre: '', apellido: '', cedula: '', password: '',
    sucursalId: '', isAdmin: false, isActive: true
  });
  const [isEditing, setIsEditing] = useState(false);

  const [search, setSearch] = useState('');
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersData, branchesData] = await Promise.all([
        UserService.getAllUsers(),
        BranchService.getBranches()
      ]);
      setUsers(usersData);
      setBranches(branchesData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este usuario?')) {
      try { await UserService.deleteUser(id); fetchData(); }
      catch (e) { console.error(e); }
    }
  };

  const handleEdit = (user) => {
    setCurrentUser(user); setIsEditing(true); setShowModal(true);
  };

  const handleView = (user) => {
    navigate(`/home-admin/user-details/${user.id}`);
  };

  const handleNew = () => {
    setCurrentUser({ nombre:'', apellido:'', cedula:'', password:'', sucursalId:'', isAdmin:false, isActive:true });
    setIsEditing(false); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) await UserService.updateUser(String(currentUser.id), { ...currentUser });
      else await UserService.registerUser({ ...currentUser });
      setShowModal(false); fetchData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar usuario.');
    }
  };

  const getBranchName = (sucursalId) => {
    const b = branches.find(b => b.id === sucursalId);
    return b ? b.nombre : '—';
  };

  const filteredUsers = users
    .filter(u => {
      const term = search.toLowerCase();
      const matchesSearch =
        u.nombre.toLowerCase().includes(term) ||
        u.apellido.toLowerCase().includes(term) ||
        u.cedula.toLowerCase().includes(term);
      const matchesAdmin = filterAdmin === 'all' ? true : filterAdmin === 'admin' ? u.isAdmin : !u.isAdmin;
      const matchesStatus = filterStatus === 'all' ? true : filterStatus === 'active' ? u.isActive : !u.isActive;
      return matchesSearch && matchesAdmin && matchesStatus;
    })
    .sort((a, b) => {
      const da = new Date(a.createdAt), db = new Date(b.createdAt);
      return sortOrder === 'asc' ? da - db : db - da;
    });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  const totalUsers  = users.length;
  const adminCount  = users.filter(u => u.isAdmin).length;
  const activeCount = users.filter(u => u.isActive).length;

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Spinner animation="border" style={{ color: '#2563eb' }} />
    </div>
  );

  return (
    <div className="manage-users-root">
      <div className="container-fluid p-3 p-md-4">

        {/* ── HEADER ── */}
        <div className="page-header mb-4">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
            <div>
              <h3 className="mb-1">Gestión de Usuarios</h3>
              <p className="mb-0">Administra y controla los accesos del sistema</p>
            </div>
            <button className="btn-add-user" onClick={handleNew}>
              <UserPlus size={16} />
              <span>Nuevo Usuario</span>
            </button>
          </div>
        </div>

        {/* ── STAT CARDS ── */}
        <div className="row g-3 mb-4" style={{ marginTop: '1rem' }}>
          {[
            { label: 'Total Usuarios', value: totalUsers, icon: <Users size={20} color="#2563eb" />, bg: '#eff6ff' },
            { label: 'Administradores', value: adminCount, icon: <ShieldCheck size={20} color="#dc2626" />, bg: '#fee2e2' },
            { label: 'Usuarios Activos', value: activeCount, icon: <User size={20} color="#16a34a" />, bg: '#dcfce7' },
          ].map((s, i) => (
            <div key={i} className="col-12 col-sm-4">
              <div className="stat-card d-flex align-items-center gap-3">
                <div className="icon-wrap" style={{ background: s.bg }}>{s.icon}</div>
                <div>
                  <div className="stat-value">{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── FILTER BAR ── */}
        <div className="filter-bar mb-4">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="search-wrap flex-grow-1" style={{ minWidth: '180px', maxWidth: '280px' }}>
              <Search size={15} className="search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Buscar nombre, apellido o cédula…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>

            <select className="form-select" style={{ width: '145px' }} value={filterAdmin}
              onChange={e => { setFilterAdmin(e.target.value); setPage(1); }}>
              <option value="all">Todos los roles</option>
              <option value="admin">Admins</option>
              <option value="user">Usuarios</option>
            </select>

            <select className="form-select" style={{ width: '145px' }} value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
              <option value="all">Todo estado</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>

            <select className="form-select" style={{ width: '130px' }} value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}>
              <option value="desc">Fecha ↓</option>
              <option value="asc">Fecha ↑</option>
            </select>

            <select className="form-select" style={{ width: '100px' }} value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n} / pág</option>)}
            </select>
          </div>
        </div>

        {/* ── TABLE ── */}
        <div className="table-card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th className="d-none d-md-table-cell">Cédula</th>
                  <th className="d-none d-lg-table-cell">Sucursal</th>
                  <th>Rol</th>
                  <th className="d-none d-sm-table-cell">Estado</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="empty-state">
                        <Users size={48} />
                        <p className="mt-2 mb-0">No se encontraron usuarios</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="user-avatar">{getInitials(user.nombre, user.apellido)}</div>
                          <div>
                            <div className="fw-semibold" style={{ color: '#0f172a', fontSize: '.875rem' }}>
                              {user.nombre} {user.apellido}
                            </div>
                            <div className="text-muted d-md-none" style={{ fontSize: '.75rem' }}>{user.cedula}</div>
                            <div className="text-muted d-lg-none d-none d-sm-block" style={{ fontSize: '.75rem' }}>
                              {user.sucursal?.nombre || getBranchName(user.sucursalId)}
                            </div>
                            <div className="d-sm-none" style={{ fontSize: '.75rem' }}>
                              <span className={`status-badge ${user.isActive ? 'status-active' : 'status-inactive'}`}>
                                {user.isActive ? 'Activo' : 'Inactivo'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="d-none d-md-table-cell" style={{ color: '#64748b' }}>{user.cedula}</td>
                      <td className="d-none d-lg-table-cell" style={{ color: '#64748b' }}>
                        {user.sucursal?.nombre || getBranchName(user.sucursalId)}
                      </td>
                      <td>
                        <span className={`role-badge ${user.isAdmin ? 'role-admin' : 'role-user'}`}>
                          {user.isAdmin ? 'Admin' : 'Usuario'}
                        </span>
                      </td>
                      <td className="d-none d-sm-table-cell">
                        <span className={`status-badge ${user.isActive ? 'status-active' : 'status-inactive'}`}>
                          {user.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-2">
                          <button className="btn-action view" onClick={() => handleView(user)}><Eye size={13} />
                            <h4 className="d-none d-md-inline">Ver</h4>
                          </button>
                          <button className="btn-action edit" onClick={() => handleEdit(user)}>
                            <Edit size={13} />
                            <h4 className="d-none d-md-inline">Editar</h4>
                          </button>
                          <button className="btn-action delete" onClick={() => handleDelete(user.id)}>
                            <Trash2 size={13} />
                            <h4 className="d-none d-md-inline">Eliminar</h4>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── PAGINATION ── */}
        <div className="pagination-wrap d-flex justify-content-between align-items-center mt-3">
          <span>
            {filteredUsers.length === 0
              ? 'Sin resultados'
              : `Mostrando ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filteredUsers.length)} de ${filteredUsers.length}`}
          </span>
          <div className="d-flex align-items-center gap-2">
            <button className="btn-page" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={14} />
            </button>
            <span className="fw-600">Pág. {page} / {totalPages}</span>
            <button className="btn-page" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── MODAL ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              {isEditing ? <Edit size={18} /> : <UserPlus size={18} />}
              {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <Form.Group>
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control type="text" value={currentUser.nombre}
                    onChange={e => setCurrentUser({ ...currentUser, nombre: e.target.value })} required />
                </Form.Group>
              </div>
              <div className="col-12 col-sm-6">
                <Form.Group>
                  <Form.Label>Apellido</Form.Label>
                  <Form.Control type="text" value={currentUser.apellido}
                    onChange={e => setCurrentUser({ ...currentUser, apellido: e.target.value })} required />
                </Form.Group>
              </div>
              <div className="col-12 col-sm-6">
                <Form.Group>
                  <Form.Label>Cédula</Form.Label>
                  <Form.Control type="text" value={currentUser.cedula}
                    onChange={e => setCurrentUser({ ...currentUser, cedula: e.target.value })} required />
                </Form.Group>
              </div>
              {!isEditing && (
                <div className="col-12 col-sm-6">
                  <Form.Group>
                    <Form.Label>Contraseña</Form.Label>
                    <Form.Control type="password" value={currentUser.password}
                      onChange={e => setCurrentUser({ ...currentUser, password: e.target.value })} required />
                  </Form.Group>
                </div>
              )}
              <div className="col-12">
                <Form.Group>
                  <Form.Label>Sucursal</Form.Label>
                  <Form.Select value={currentUser.sucursalId || ''}
                    onChange={e => setCurrentUser({ ...currentUser, sucursalId: e.target.value })} required>
                    <option value="">Seleccione una sucursal</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-12 col-sm-6">
                <div className="toggle-row">
                  <label htmlFor="toggle-admin">Administrador</label>
                  <Form.Check id="toggle-admin" type="switch" className="form-switch"
                    checked={currentUser.isAdmin}
                    onChange={e => setCurrentUser({ ...currentUser, isAdmin: e.target.checked })} />
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div className="toggle-row">
                  <label htmlFor="toggle-active">Usuario Activo</label>
                  <Form.Check id="toggle-active" type="switch" className="form-switch"
                    checked={currentUser.isActive}
                    onChange={e => setCurrentUser({ ...currentUser, isActive: e.target.checked })} />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" style={{ borderRadius: '.625rem', fontWeight: 600 }}
              onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit"
              style={{ background: '#2563eb', border: 'none', borderRadius: '.625rem', fontWeight: 600, padding: '.5rem 1.5rem' }}>
              Guardar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}